<template>
  <div class="container">
    <div class="chat-container">
      <SubtitleContent />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, provide, ref, watch, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { cloneDeep, isEmpty } from 'lodash';
import { storeToRefs } from 'pinia';

import SubtitleContent from '@/components/domains/chat/chat-subtitle/SubtitleContent.vue';
import { useSocketSubtitle } from '@/composables/domains/chat/useSocketSubtitle';
import useHotkey from '@/composables/shared/useHotkey';
import { useLanguage } from '@/composables/shared/useLanguage';
import { usePreferences } from '@/composables/shared/usePreferences';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast.js';
import { LanguageCode } from '@/types/common';
import { SocketState } from '@/utils/enums';
import {
  ij_chatId,
  ij_endCall,
  ij_isEndedCall,
  ij_isVisibleButtonMyLanguage,
  ij_settingsSubtitleCached,
} from '@/utils/injectionKeys';

const { t } = useI18n();
const route = useRoute();

const socketStore = useSocketStore();
const toastStore = useToastStore();

const { changeLanguage } = useLanguage();
const { assimilateUserPreferences } = usePreferences();
useHotkey();

const { socketState } = storeToRefs(socketStore);

const chatId = ref(null);
const isVisibleButtonMyLanguage = ref(true); // 나의 언어만 보기 버튼 보임 여부
const isEndedCall = ref(false); // 수동적으로 통역가 종료되었는지 여부
const settingsSubtitleCached = reactive({});

const { connectToWebsocket } = useSocketSubtitle(chatId);

let intervalReconnect = 0;

const endCall = () => {
  isEndedCall.value = true;
};

const applySettingsQuery = () => {
  const settingsQuery = cloneDeep(route.query);
  delete settingsQuery.code;

  if (isEmpty(settingsQuery)) return;

  const settingsNumeric = [
    'textSizeMultiplier',
    'subtitleOpacity',
    'subtitleLocationY',
    'subtitleLineHeight',
    'subtitleLineCount',
    'subtitleWidth',
  ];

  Object.entries(settingsQuery).forEach(([key, value]) => {
    if (settingsNumeric.includes(key)) {
      const newVal = parseFloat(value as string);
      settingsQuery[key] = newVal;
    }
  });

  assimilateSettings(settingsQuery);
};

const assimilateSettings = (settings) => {
  const resAssimilation = assimilateUserPreferences(settings);
  const languageDefault: LanguageCode = ((resAssimilation ? settings.languageCode : 'ko') as LanguageCode) ?? 'ko';

  changeLanguage(languageDefault);
};

const applySettingsCached = () => {
  const settingsCachedSubtitle = localStorage.getItem('settings_subtitle');

  if (!settingsCachedSubtitle) {
    changeLanguage('ko');
    return;
  }

  const settingsCachedParsed = JSON.parse(settingsCachedSubtitle);
  assimilateSettings(settingsCachedParsed);
};

const updateCachedSettings = () => {
  const cachedSettings = localStorage.getItem('settings_subtitle');

  if (!cachedSettings) return null;

  Object.assign(settingsSubtitleCached, JSON.parse(cachedSettings));
};

onMounted(async () => {
  updateCachedSettings();

  await connectToWebsocket();
  applySettingsCached();
  applySettingsQuery();
});

watch(
  socketState,
  (newVal, oldVal) => {
    switch (newVal) {
      case SocketState.OPEN:
        clearInterval(intervalReconnect);
        intervalReconnect = 0;
        break;
      case SocketState.CLOSED:
        // 고의로 연결을 끊은 것이 아닐 경우
        if (!isEndedCall.value) {
          if (oldVal === SocketState.OPEN) {
            toastStore.error(t('try_reconnection'), 'RECONNECT');
          }
          clearInterval(intervalReconnect);
          intervalReconnect = setInterval(async () => {
            await connectToWebsocket();
          }, 5000);
        }
        break;
    }
  },
  { immediate: true }
);

provide(ij_chatId, chatId);
provide(ij_isVisibleButtonMyLanguage, isVisibleButtonMyLanguage);
provide(ij_endCall, endCall);
provide(ij_isEndedCall, isEndedCall);
provide(ij_settingsSubtitleCached, updateCachedSettings);
</script>

<style lang="scss" scoped>
.container {
  width: 100%;
  height: 100dvh;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.chat-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  overflow: hidden;
}
</style>
